/**
 * Dados e ações do Detalhe OS 2 — mesmos hooks/APIs do OrderDetailsModalV2.
 * Não altera o V2; só reutiliza a camada já existente.
 */

import React from 'react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { hasDefeitoAlert, normalizeHexColor } from '../utils/defeitoAlertBadge';
import { getDefeitoDisplayState } from '../utils/orderDetailsDefeitoDisplay';
import { getBoardClienteExibicao } from '../utils/clienteBoardDisplay';
import {
  formatSeverityLabel,
  contrastTextOnHex,
  getSLAStatus,
  buildSlaStatusMessage,
  formatPhoneDisplay,
  formatPedidoTimelineDateBr,
} from '../utils/orderDetailsModalUtils';
import { formatOrderStreetAddress, parseOrderAddressLines } from '../utils/orderDetailsAddressUtils';
import { useOrderDetailsAddressEdit } from './useOrderDetailsAddressEdit';
import { useOrderDetailsSolicitanteEdit } from './useOrderDetailsSolicitanteEdit';
import { useOrderDetailsAuditLogs } from './useOrderDetailsAuditLogs';
import { useOrderDetailsHistorico } from './useOrderDetailsHistorico';
import { useOrderDetailsNotes } from './useOrderDetailsNotes';
import { useOrderDetailsDefeitoMotivoEdit } from './useOrderDetailsDefeitoMotivoEdit';
import { useOrderDetailsTipoOsEdit } from './useOrderDetailsTipoOsEdit';
import { useOrderDetailsReincidenciaHistorico } from './useOrderDetailsReincidenciaHistorico';
import { useOrderDetailsPedidoPanel } from './useOrderDetailsPedidoPanel';
import { useOrderDetailsPedidoDetalhe } from './useOrderDetailsPedidoDetalhe';
import { returnOrderToPendente, fetchOrderAttachments } from '../services/boardOrderService';
import { technicianInitials } from '../utils/technicianInitials';
import { formatNoteDateTime } from '../components/OrderDetailsNotesPanel';
import {
  formatReincidenciaDataAbertura,
  getReincidenciaStepOsLabel,
} from '../utils/orderDetailsReincidenciaUtils';
import { generateOSPdfV2Download, generateOSPdfV2FromOrder } from '../../new-os/utils/osPrintPdf';

const ATENDIMENTOS_HISTORICO_ANIM_MS = 280;

function formatLogDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function formatHistoricoDate(dataStr, user) {
  const base = formatReincidenciaDataAbertura(dataStr, true);
  const who = String(user ?? '').trim();
  if (base === '—' && !who) return '';
  if (base === '—') return who;
  return who ? `${base} · ${who}` : base;
}

function dash(value, fallback = '—') {
  const s = value == null ? '' : String(value).trim();
  return s || fallback;
}

export function useOrderDetailsMockController(
  order,
  {
    onMotivoUpdated,
    onTipoUpdated,
    onAddressUpdated,
    onLocalizacaoUpdated,
    onSolicitanteUpdated,
    isAdminUser = false,
    hasCustomersModule = false,
    onReturnToPendenteSuccess,
    codstatusRetorno = '',
    statusUptodate = '',
  } = {},
) {
  const { auditLogs, loadingAuditLogs, auditLogsError, openLogsView } = useOrderDetailsAuditLogs(order);
  const { historicoSteps, historicoCycles, loadingHistorico, historicoError, openHistoricoView } =
    useOrderDetailsHistorico(order);
  const {
    notes: orderNotes,
    hasNotes,
    loadingNotes,
    savingNote,
    notesError,
    saveError,
    fetchNotes,
    submitNote,
  } = useOrderDetailsNotes(order);

  const [defeitoData, setDefeitoData] = React.useState(null);
  const [loadingDefeito, setLoadingDefeito] = React.useState(false);
  const [tecnicosTerritorio, setTecnicosTerritorio] = React.useState(null);
  const [returnToPendenteBusy, setReturnToPendenteBusy] = React.useState(false);
  const [returnToPendenteFeedback, setReturnToPendenteFeedback] = React.useState(null);
  const [loadingTecnicosTerritorio, setLoadingTecnicosTerritorio] = React.useState(false);
  const [tecnicosTerritorioError, setTecnicosTerritorioError] = React.useState(null);
  const [linkedPedidosList, setLinkedPedidosList] = React.useState([]);
  const [loadingLinkedPedidos, setLoadingLinkedPedidos] = React.useState(false);
  const [solicitanteEmail, setSolicitanteEmail] = React.useState(null);
  const [showSugestaoSolucao, setShowSugestaoSolucao] = React.useState(false);
  const [anexosFiles, setAnexosFiles] = React.useState([]);
  const [anexosLoading, setAnexosLoading] = React.useState(false);
  const [anexosError, setAnexosError] = React.useState(null);
  const [standbyNotesPopoverOpen, setStandbyNotesPopoverOpen] = React.useState(false);
  const [atendimentosHistoricoOpen, setAtendimentosHistoricoOpen] = React.useState(false);
  const [atendimentosHistoricoMounted, setAtendimentosHistoricoMounted] = React.useState(false);
  const [printBusy, setPrintBusy] = React.useState(null);
  const atendimentosHistoricoCloseTimerRef = React.useRef(null);
  const standbyNotesAnchorRef = React.useRef(null);

  const closeAtendimentosHistorico = React.useCallback(() => {
    setAtendimentosHistoricoOpen(false);
    if (atendimentosHistoricoCloseTimerRef.current) {
      clearTimeout(atendimentosHistoricoCloseTimerRef.current);
    }
    atendimentosHistoricoCloseTimerRef.current = setTimeout(() => {
      setAtendimentosHistoricoMounted(false);
      atendimentosHistoricoCloseTimerRef.current = null;
    }, ATENDIMENTOS_HISTORICO_ANIM_MS);
  }, []);

  const openAtendimentosHistorico = React.useCallback(() => {
    if (atendimentosHistoricoCloseTimerRef.current) {
      clearTimeout(atendimentosHistoricoCloseTimerRef.current);
      atendimentosHistoricoCloseTimerRef.current = null;
    }
    setAtendimentosHistoricoMounted(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setAtendimentosHistoricoOpen(true));
    });
  }, []);

  const handleHistoricoServicoClick = React.useCallback(
    (e) => {
      e?.stopPropagation?.();
      if (atendimentosHistoricoMounted) closeAtendimentosHistorico();
      else openAtendimentosHistorico();
    },
    [atendimentosHistoricoMounted, closeAtendimentosHistorico, openAtendimentosHistorico],
  );

  const osAnteriorForReincidencia = order?.TB02115_OS ?? order?.tb02115_os ?? order?.TB02115_os;
  const isOrderReincidencia = String(osAnteriorForReincidencia ?? '').trim().length > 0;

  const defeitoMotivoEdit = useOrderDetailsDefeitoMotivoEdit(order, { onMotivoUpdated });
  const tipoOsEdit = useOrderDetailsTipoOsEdit(order, { isAdmin: isAdminUser, onTipoUpdated });
  const addressEdit = useOrderDetailsAddressEdit(order, { onAddressUpdated, onLocalizacaoUpdated });
  const solicitanteEdit = useOrderDetailsSolicitanteEdit(order, { onSolicitanteUpdated, setSolicitanteEmail });

  React.useEffect(() => {
    setShowSugestaoSolucao(false);
    setStandbyNotesPopoverOpen(false);
    setAtendimentosHistoricoOpen(false);
    setAtendimentosHistoricoMounted(false);
    setAnexosFiles([]);
    setAnexosError(null);
    setReturnToPendenteFeedback(null);
    if (atendimentosHistoricoCloseTimerRef.current) {
      clearTimeout(atendimentosHistoricoCloseTimerRef.current);
      atendimentosHistoricoCloseTimerRef.current = null;
    }
  }, [order?.id]);

  React.useEffect(() => {
    if (!order?.id) return undefined;
    let cancelled = false;
    fetchNotes().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [order?.id, fetchNotes]);

  React.useEffect(() => {
    const orderId = order?.id ?? order?.TB02115_CODIGO;
    if (!hasCustomersModule || !orderId) {
      setAnexosFiles([]);
      setAnexosError(null);
      setAnexosLoading(false);
      return undefined;
    }
    let cancelled = false;
    setAnexosLoading(true);
    fetchOrderAttachments(orderId).then((res) => {
      if (cancelled) return;
      if (res.success) {
        setAnexosFiles(res.data);
        setAnexosError(null);
      } else {
        setAnexosFiles([]);
        setAnexosError(res.message || 'Erro ao carregar anexos');
      }
      setAnexosLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [hasCustomersModule, order?.id, order?.TB02115_CODIGO]);

  React.useEffect(() => {
    if (!order || !(Number(order.pedidos_vinculados) > 1)) {
      setLinkedPedidosList([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingLinkedPedidos(true);
    const token = localStorage.getItem('authToken');
    fetch(`${getApiBaseUrl()}/api/orders/${order.id}/linked-pedidos`, {
      headers: { Authorization: token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.success && Array.isArray(data.data?.pedidos)) {
          setLinkedPedidosList(data.data.pedidos);
        } else if (!cancelled) {
          setLinkedPedidosList([]);
        }
      })
      .catch(() => {
        if (!cancelled) setLinkedPedidosList([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingLinkedPedidos(false);
      });
    return () => {
      cancelled = true;
    };
  }, [order?.id, order?.pedidos_vinculados]);

  React.useEffect(() => {
    setSolicitanteEmail(null);
    if (!order?.id) return undefined;
    let cancelled = false;
    const token = localStorage.getItem('authToken');
    fetch(`${getApiBaseUrl()}/api/orders/${order.id}/solicitante`, {
      headers: { Authorization: token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const em = data?.success && data?.data?.email ? String(data.data.email).trim() : '';
        setSolicitanteEmail(em || null);
      })
      .catch(() => {
        if (!cancelled) setSolicitanteEmail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [order?.id]);

  React.useEffect(() => {
    if (!order?.id) return undefined;
    setDefeitoData(null);
    let cancelled = false;
    (async () => {
      setLoadingDefeito(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${getApiBaseUrl()}/api/orders/${order.id}/defeito`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const result = await response.json();
        if (cancelled) return;
        if (result.success) setDefeitoData(result.data);
      } catch {
        /* silencioso */
      } finally {
        if (!cancelled) setLoadingDefeito(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [order?.id]);

  const fetchTecnicosTerritorio = React.useCallback(async () => {
    if (!order?.id || tecnicosTerritorio) return;
    setLoadingTecnicosTerritorio(true);
    setTecnicosTerritorioError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiBaseUrl()}/api/orders/${order.id}/tecnicos-territorio`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (result.success) setTecnicosTerritorio(Array.isArray(result.data) ? result.data : []);
      else setTecnicosTerritorioError(result.message);
    } catch {
      setTecnicosTerritorioError('Erro ao conectar com o servidor');
    } finally {
      setLoadingTecnicosTerritorio(false);
    }
  }, [order?.id, tecnicosTerritorio]);

  const handleReturnToPendente = React.useCallback(async () => {
    const numeroOS = String(order?.id ?? order?.TB02115_CODIGO ?? '').trim();
    if (!numeroOS || returnToPendenteBusy) return;
    setReturnToPendenteFeedback(null);
    setReturnToPendenteBusy(true);
    try {
      const result = await returnOrderToPendente(numeroOS);
      if (!result.success) {
        setReturnToPendenteFeedback({
          type: 'error',
          message: result.message || 'Não foi possível retornar a OS para pendência.',
        });
        return;
      }
      setReturnToPendenteFeedback({
        type: 'success',
        message: result.message || 'OS retornada para pendência com sucesso.',
      });
      if (typeof onReturnToPendenteSuccess === 'function') {
        await onReturnToPendenteSuccess({
          orderId: numeroOS,
          technician: order?.detailModalBoardTechnician || null,
        });
      }
    } catch (err) {
      setReturnToPendenteFeedback({
        type: 'error',
        message: err?.message || 'Erro ao retornar OS para pendência.',
      });
    } finally {
      setReturnToPendenteBusy(false);
    }
  }, [order, returnToPendenteBusy, onReturnToPendenteSuccess]);

  const anexosMediaFiles = React.useMemo(
    () => (anexosFiles || []).filter((f) => !f.isAudio),
    [anexosFiles],
  );
  const defeitoAudioFile = React.useMemo(
    () =>
      (anexosFiles || []).find((f) => f.isAudio || String(f.ext || '').toLowerCase() === 'webm') || null,
    [anexosFiles],
  );

  const orderForPdf = React.useMemo(() => {
    if (!order) return order;
    const nome =
      defeitoData?.defeito != null && defeitoData.defeito !== 'Não informado' ? defeitoData.defeito : undefined;
    const obs =
      defeitoData?.observacao != null && defeitoData.observacao !== 'Não informado'
        ? defeitoData.observacao
        : undefined;
    if (nome === undefined && obs === undefined) return order;
    return { ...order, defeitoNome: nome, defeitoObservacao: obs };
  }, [order, defeitoData]);

  const handleGerarPDF = React.useCallback(async () => {
    if (!order) return;
    setPrintBusy('pdf');
    try {
      await generateOSPdfV2Download(orderForPdf);
    } finally {
      setPrintBusy(null);
    }
  }, [order, orderForPdf]);

  const handleImprimir = React.useCallback(async () => {
    if (!order) return;
    setPrintBusy('print');
    try {
      await generateOSPdfV2FromOrder(orderForPdf, { openForPrint: true });
    } finally {
      setPrintBusy(null);
    }
  }, [order, orderForPdf]);

  const openGoogleMaps = React.useCallback(() => {
    const endereco = order?.endereco || '';
    if (!endereco || endereco === 'Endereço não informado' || !String(endereco).trim()) {
      window.alert('Endereço não disponível para visualização no mapa.');
      return;
    }
    let enderecoCompleto = endereco;
    const cidade = order.TB02115_CIDADE || order.cidade;
    const estado = order.TB02115_ESTADO;
    if (cidade && !enderecoCompleto.toLowerCase().includes(String(cidade).toLowerCase())) {
      enderecoCompleto += `, ${cidade}`;
    }
    if (estado && !enderecoCompleto.toLowerCase().includes(String(estado).toLowerCase())) {
      enderecoCompleto += `, ${estado}`;
    }
    if (!enderecoCompleto.toLowerCase().includes('brasil')) {
      enderecoCompleto += ', Brasil';
    }
    const mapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(enderecoCompleto)}/@?entry=ttu`;
    const w = window.open(mapsUrl, '_blank');
    if (w) {
      try {
        w.opener = null;
      } catch {
        /* ignore */
      }
    }
  }, [order]);

  if (!order) {
    return { order: null };
  }

  const {
    motivoDisplay,
    isMotivoEditing,
    motivoEditValue,
    setMotivoEditValue,
    isSavingMotivo,
    motivoSaveFeedback,
    handleStartMotivoEdit,
    handleCancelMotivoEdit,
    handleSaveMotivo,
    MOTIVO_OS_MAX,
    isDefeitoEditing,
    isObsEditing,
    defeitoSearchQuery,
    defeitoSearchResults,
    isDefeitoSearchLoading,
    defeitoSearchError,
    selectedDefeito,
    editObsValue,
    setEditObsValue,
    isSavingDefeitoObs,
    defeitoSaveFeedback,
    handleStartDefeitoEdit,
    handleStartObsEdit,
    handleDefeitoSearchChange,
    handleSelectDefeitoResult,
    handleClearSelectedDefeito,
    handleCancelDefeitoObsEdit,
    handleSaveDefeitoObs,
    isDefeitoObsConfirmOpen,
    handleCancelDefeitoObsConfirm,
    handleConfirmDefeitoObsChange,
    isObsGeralEditing,
    editObsGeralValue,
    setEditObsGeralValue,
    isSavingObsGeral,
    obsGeralSaveFeedback,
    handleStartObsGeralEdit,
    handleCancelObsGeralEdit,
    handleSaveObsGeral,
  } = defeitoMotivoEdit;

  const severityLabel = formatSeverityLabel(
    order?.defeito_severity_level || defeitoData?.defeito_severity_level,
  );
  const alertHexRaw = String(order?.color_bg ?? defeitoData?.color_bg ?? '').trim();
  const alertHex = normalizeHexColor(alertHexRaw) || alertHexRaw;
  const alertCode = String(order?.defeito_alert ?? defeitoData?.defeito_alert ?? '').trim();
  const hasAlert = Boolean(alertCode) && (hasDefeitoAlert(order) || Boolean(defeitoData?.defeito_alert));
  const alertTextColor = alertHex ? contrastTextOnHex(alertHex) : '#ffffff';
  const slaInfo = getSLAStatus(order.sla);
  const slaMessage = buildSlaStatusMessage(order, slaInfo);
  const statusName = order.TB01073_NOME || order.status || 'Não informado';
  const isRetorno =
    codstatusRetorno !== '' &&
    String(order.TB02115_STATUS || '').trim() === String(codstatusRetorno || '').trim();

  const cfgFuturaStatus = String(statusUptodate || '').trim();
  const orderStatusCode = String(order?.TB02115_STATUS || '').trim();
  const standbyNotesTooltipText = String(order?.standbyNotes ?? order?.STANDBY_NOTES ?? '').trim();
  const showStandbyNotesIndicator =
    Boolean(cfgFuturaStatus) && orderStatusCode === cfgFuturaStatus && standbyNotesTooltipText.length > 0;

  const isTecnicoTerritorio = order.TB01024_TERRITORIO === 'S';
  const canReturnToPendente = Boolean(order?.fromFuturaRetornoLiberado);

  const pedidoBlocks =
    Number(order.pedidos_vinculados) > 1 && linkedPedidosList.length > 0
      ? linkedPedidosList.map((cod) => ({ cod: String(cod) }))
      : order.pedidoVinculado
        ? [{ cod: String(order.pedidoVinculado) }]
        : [];

  const addressLines = parseOrderAddressLines(order);
  const streetAddress = formatOrderStreetAddress(order);
  const defeitoDisplay = getDefeitoDisplayState(defeitoData, motivoDisplay);
  const fone2Raw = String(order?.TB02115_FONE2 ?? '').trim();
  const celularRaw = String(order?.TB02115_CELULAR ?? '').trim();
  const fone1Raw = String(order?.TB02115_FONE ?? '').trim();
  const displayOs = String(order.id ?? order.TB02115_CODIGO ?? '').trim();

  const notesMapped = (orderNotes || []).map((item, index) => {
    const name = String(item.TB02130_USER || '—').trim() || '—';
    return {
      id: `${name}-${index}`,
      name,
      date: formatNoteDateTime(item.TB02130_DATA),
      text: item.TB02130_OBS || '—',
      initials: technicianInitials(name),
      profilePhotoUserId: item?.profilePhotoUserId ?? null,
      hasProfilePhoto: item?.hasProfilePhoto === true,
    };
  });

  const logsMapped = (auditLogs || []).map((log, index) => {
    const data = log.data && typeof log.data === 'object' ? log.data : {};
    return {
      n: (auditLogs || []).length - index,
      datetime: formatLogDateTime(log.timestamp),
      usuario: log.user_name || '—',
      origem: data.origem || '—',
      destino: data.destino || '—',
      tecAtual: data.tecnicoCurrent || '—',
      tecNovo: data.tecnicoNovo || '—',
      sessaoOrigem: data.sessaoOrigem || '—',
      sessaoDestino: data.sessaoDestino || '—',
    };
  });

  const historicoMapped = (historicoSteps || []).map((step, index) => ({
    id: `${step.TB02130_NOME || 'step'}-${index}`,
    label: dash(step.TB02130_NOME, '—'),
    date: formatHistoricoDate(step.TB02130_DATA, step.TB02130_USER),
    observacoes: String(step.TB02130_OBS ?? '').trim(),
    cycle: step.cycle,
  }));

  const onAudioTranscribed = (data) => {
    const nextObs = data?.observacaoGeral;
    if (nextObs == null) return;
    setDefeitoData((prev) => (prev ? { ...prev, observacaoGeral: nextObs } : { observacaoGeral: nextObs }));
    setEditObsGeralValue(String(nextObs));
  };

  return {
    order,
    displayOs,
    cliente: dash(getBoardClienteExibicao(order), 'Não informado'),
    contrato: dash(order.contrato, 'Não informado'),
    equipamento: dash(order.equipamento),
    numeroSerie: dash(order.numeroSerie || order.TB02115_NUMSERIE),
    patrimonio: dash(order.patrimonio || order.TB02112_PAT),
    tecnico: dash(order.TB01024_NOME || order.tecnico, 'Não informado'),
    tecmaq: dash(order.TECMAQ, 'Não informado'),
    statusName,
    isRetorno,
    isOrderReincidencia,
    nativeReturnCount: order?.source === 'native' ? Number(order.returnCount) || 0 : 0,
    nativeCycle: order?.source === 'native'
      ? Math.max(1, Number(order.currentCycle) || (Number(order.returnCount) || 0) + 1)
      : 0,
    isTecnicoTerritorio,
    canReturnToPendente,
    returnToPendenteBusy,
    returnToPendenteFeedback,
    handleReturnToPendente,
    showStandbyNotesIndicator,
    standbyNotesTooltipText,
    standbyNotesPopoverOpen,
    setStandbyNotesPopoverOpen,
    standbyNotesAnchorRef,
    slaInfo,
    slaMessage,
    previsao: order.previsao,
    severityLabel,
    alertHex,
    alertCode,
    hasAlert,
    alertTextColor,
    streetAddress,
    addressLines,
    addressEdit,
    solicitanteEdit,
    solicitante: dash(order.solicitante, 'Não informado'),
    solicitanteEmail,
    fone1Display: fone1Raw ? formatPhoneDisplay(fone1Raw) : '',
    fone2Display: fone2Raw ? formatPhoneDisplay(fone2Raw) : '',
    celularDisplay: celularRaw ? formatPhoneDisplay(celularRaw) : '',
    celularWaHref: celularRaw ? `https://wa.me/55${celularRaw.replace(/\D/g, '')}` : '',
    openGoogleMaps,
    tipoOsEdit,
    defeitoMotivoEdit,
    motivoDisplay: dash(motivoDisplay, ''),
    isMotivoEditing,
    motivoEditValue,
    setMotivoEditValue,
    isSavingMotivo,
    motivoSaveFeedback,
    handleStartMotivoEdit,
    handleCancelMotivoEdit,
    handleSaveMotivo,
    MOTIVO_OS_MAX,
    isDefeitoEditing,
    defeitoSearchQuery,
    defeitoSearchResults,
    isDefeitoSearchLoading,
    defeitoSearchError,
    selectedDefeito,
    handleStartDefeitoEdit: () => handleStartDefeitoEdit(defeitoData),
    handleDefeitoSearchChange,
    handleSelectDefeitoResult,
    handleClearSelectedDefeito,
    isObsEditing,
    editObsValue,
    setEditObsValue,
    isSavingDefeitoObs,
    defeitoSaveFeedback,
    handleStartObsEdit: () => handleStartObsEdit(defeitoData),
    handleCancelDefeitoObsEdit: () => handleCancelDefeitoObsEdit(defeitoData),
    handleSaveDefeitoObs: () => handleSaveDefeitoObs(defeitoData, setDefeitoData),
    isDefeitoObsConfirmOpen,
    handleCancelDefeitoObsConfirm,
    handleConfirmDefeitoObsChange: () => handleConfirmDefeitoObsChange(defeitoData, setDefeitoData),
    isObsGeralEditing,
    editObsGeralValue,
    setEditObsGeralValue,
    isSavingObsGeral,
    obsGeralSaveFeedback,
    handleStartObsGeralEdit: () => handleStartObsGeralEdit(defeitoData),
    handleCancelObsGeralEdit: () => handleCancelObsGeralEdit(defeitoData),
    handleSaveObsGeral: () => handleSaveObsGeral(defeitoData, setDefeitoData),
    defeitoData,
    loadingDefeito,
    defeitoDisplay,
    defeitoNome: loadingDefeito && !defeitoData
      ? 'Carregando…'
      : defeitoDisplay.obsGeralProminent
        ? 'Sem defeito'
        : dash(defeitoData?.defeito, 'Defeito não informado'),
    defeitoObservacao: dash(defeitoData?.observacao, ''),
    obsGeralText: defeitoDisplay.obsGeral,
    hasCustomersModule,
    anexosMediaFiles,
    anexosLoading,
    anexosError,
    defeitoAudioFile,
    onAudioTranscribed,
    notesMapped,
    hasNotes,
    notesCount: notesMapped.length,
    loadingNotes,
    savingNote,
    notesError,
    saveError,
    submitNote,
    logsMapped,
    loadingAuditLogs,
    auditLogsError,
    openLogsView,
    historicoMapped,
    historicoSteps,
    historicoCycles,
    loadingHistorico,
    historicoError,
    openHistoricoView,
    pedidoBlocks,
    loadingLinkedPedidos,
    tecnicosTerritorio: Array.isArray(tecnicosTerritorio) ? tecnicosTerritorio : [],
    loadingTecnicosTerritorio,
    tecnicosTerritorioError,
    fetchTecnicosTerritorio,
    showSugestaoSolucao,
    setShowSugestaoSolucao,
    atendimentosHistoricoOpen,
    atendimentosHistoricoMounted,
    handleHistoricoServicoClick,
    closeAtendimentosHistorico,
    printBusy,
    handleGerarPDF,
    handleImprimir,
  };
}

export function useOrderDetailsMockPedido(codigoPedido, enabled, native = false) {
  const panel = useOrderDetailsPedidoPanel(codigoPedido, enabled, native);
  const detalhe = useOrderDetailsPedidoDetalhe(codigoPedido, enabled, native);

  const produtos = React.useMemo(() => {
    const raw = detalhe.data?.produtos?.length
      ? detalhe.data.produtos
      : panel.pedido?.produtos || [];
    return raw.map((prod, index) => {
      const codigo = dash(prod.codprod ?? prod.codigo);
      const edisp = prod.qtdeComDisp ?? prod.edisp;
      const edispNum = edisp == null || edisp === '' ? null : Number(edisp);
      return {
        key: `${codigo}-${index}`,
        codigo,
        referencia: dash(prod.referencia),
        produto: dash(prod.produto ?? prod.nome, '—'),
        fabricante: dash(prod.fabricante, ''),
        qtde: prod.qtde ?? prod.quantidade ?? '—',
        edisp: edisp == null || edisp === '' ? '—' : edisp,
        edispColor: Number.isFinite(edispNum) && edispNum < 0 ? '#DC2626' : '#1F2130',
      };
    });
  }, [detalhe.data, panel.pedido]);

  const processo = React.useMemo(() => {
    const mapStep = (step, index) => ({
      key: `${step.TB02130_NOME}-${index}`,
      label: dash(step.TB02130_NOME),
      date: formatPedidoTimelineDateBr(step.TB02130_DATA),
      author: dash(step.TB02130_USER, ''),
    });
    const hist = detalhe.data?.statusHistorico;
    // Detalhe completo vem ASC; timeline da API já vem DESC (atual → anteriores).
    if (Array.isArray(hist) && hist.length) {
      return hist.slice().reverse().map(mapStep);
    }
    return (panel.timeline || []).map(mapStep);
  }, [detalhe.data, panel.timeline]);

  const statusAtual = panel.statusAtual
    ? {
        code: dash(panel.statusAtual.TB02130_STATUS, ''),
        label: dash(panel.statusAtual.TB02130_NOME),
        date: formatPedidoTimelineDateBr(panel.statusAtual.TB02130_DATA),
      }
    : processo.length
      ? { code: '', label: processo[0].label, date: processo[0].date }
      : null;

  return {
    codigo: panel.codigo,
    loading: panel.loading || detalhe.loading,
    error: panel.error || detalhe.error,
    notaFiscal: detalhe.data?.nota ?? panel.pedido?.notaFiscal ?? null,
    observacoes: dash(detalhe.data?.obsNf ?? panel.pedido?.observacoes, ''),
    produtos,
    processo,
    statusAtual,
  };
}

export function useOrderDetailsMockReincidencia(orderId, enabled) {
  const { items, loading, error } = useOrderDetailsReincidenciaHistorico(orderId, enabled);
  const currentId = String(orderId ?? '').trim();
  const reincidencias = (items || []).map((item) => {
    const osLabel = getReincidenciaStepOsLabel(item, currentId);
    const osAtual = String(item.OS_Atual ?? '').trim();
    const isCurrent = Boolean(currentId && osAtual === currentId);
    return {
      os: osLabel,
      date: formatReincidenciaDataAbertura(item.TB02115_DATA),
      tag: isCurrent ? '(Atual)' : '',
      dotColor: isCurrent ? '#5B52E8' : '#C7C9D6',
    };
  });
  return { reincidencias, loading, error };
}
